import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import type { ReportLang } from '@/utils/helper/reportLang'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse, PaginationResponseWithTotals } from '../model'
import {
  SaleMemberData,
  SaleCommissionSummaryData,
  SaleCommissionSummarySource,
  SaleCommissionSummarySourceKey,
  SaleCommissionSummaryStaffRow,
  SaleOverviewData,
  SalePerformanceCommissionOverviewData,
  SalePerformanceCurrentSalesData,
  SalePerformanceData,
  SalePerformancePackageTypeCommissionsData,
  SalePerformancePackageTypeSalesData,
  SalePerformanceReceiptCommissionData,
  SalePerformanceValueData,
  SalePortfolioLeadRowData,
  SalePortfolioListSummary,
  SalePortfolioMemberRowData,
  SalePortfolioReassignPayload,
  SalePortfolioReassignResponse,
  SalePortfolioStaffData,
  SaleTodoData,
  UpdateSalePerformanceTargetPayload,
} from './model'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const POSTGREST_BASE = '/postgrest'
const COMMISSION_SUMMARY_PAGE_SIZE = 500
const DAY_IN_MS = 24 * 60 * 60 * 1000

const COMMISSION_SUMMARY_SOURCES: Array<Pick<SaleCommissionSummarySource, 'source' | 'label'>> = [
  { source: 'MEMBERSHIP', label: 'Membership' },
  { source: 'PERSONAL_TRAINER', label: 'Personal Trainer' },
  { source: 'SWIMMING', label: 'Swimming' },
  { source: 'GROUP_CLASS', label: 'Group Class' },
  { source: 'FACILITY', label: 'Facility' },
  { source: 'PRODUCTS', label: 'Products' },
]

const satangToBahtValue = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed / 100 : 0
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toText = (value: unknown): string => (typeof value === 'string' ? value : '')

const toDayTime = (value: unknown): number | null => {
  if (typeof value !== 'string' || value.length === 0) return null

  const parsed = new Date(`${value.slice(0, 10)}T00:00:00.000Z`).getTime()
  return Number.isFinite(parsed) ? parsed : null
}

const validDays = (values: Array<number | null>): number[] =>
  values.filter((value): value is number => Number.isFinite(value))

const maxDay = (...values: Array<number | null>) => Math.max(...validDays(values))

const minDay = (...values: Array<number | null>) => Math.min(...validDays(values))

const inclusiveDays = (start: number, end: number): number =>
  end < start ? 0 : Math.floor((end - start) / DAY_IN_MS) + 1

const toTotal = (pagination: Record<string, unknown> | undefined, fallback: number) => {
  const total = Number(pagination?.total)
  return Number.isFinite(total) ? total : fallback
}

const toCommissionSummarySource = (value: unknown): SaleCommissionSummarySourceKey => {
  const source = toText(value) as SaleCommissionSummarySourceKey
  return COMMISSION_SUMMARY_SOURCES.some((item) => item.source === source) ? source : 'MEMBERSHIP'
}

export const SaleService = (client: AxiosInstance) => {
  const fetchPostgrestRows = async <T>({
    resource,
    select,
    order,
    filters,
  }: {
    resource: string
    select: string
    order?: string
    filters?: string[]
  }): Promise<T[]> => {
    const fetchPage = (page: number, withCount: boolean) => {
      const query = new URLSearchParams()
      query.append('select', select)
      if (withCount) query.append('count', 'true')
      query.append('page', String(page))
      query.append('pageSize', String(COMMISSION_SUMMARY_PAGE_SIZE))
      if (order) query.append('order', order)
      ;(filters ?? []).forEach((filter) => query.append('filter', filter))

      return wrapApiCall(() =>
        client.get<{ data?: T[]; pagination?: Record<string, unknown> }>(
          `${POSTGREST_BASE}/${resource}?${query.toString()}`
        )
      )
    }

    // Page 1 requests the count so we know the total up front; the
    // remaining pages skip `count=true` (the backend would otherwise re-run
    // the full view just to recompute an unchanging total) and are fetched
    // in parallel instead of sequentially, since the total is already known.
    const { data: firstData } = await fetchPage(1, true)
    const firstRows = firstData?.data ?? []

    const total = toTotal(firstData?.pagination, firstRows.length)
    const totalPages = clampTotalPages(
      total,
      COMMISSION_SUMMARY_PAGE_SIZE,
      'sale.fetchPostgrestRows'
    )

    if (totalPages <= 1) {
      return firstRows
    }

    const restPages = await fetchPagesInBatches(
      Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
      (page) => fetchPage(page, false).then(({ data }) => data?.data ?? [])
    )

    return [firstRows, ...restPages].flat()
  }

  return {
    async getSalePerformance(id: string | number): Promise<SalePerformanceData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/sale-performances/${id}`)
      )

      return deserialize(data, SalePerformanceData)
    },
    async getSalePerformanceCurrentSales(
      id: string | number
    ): Promise<SalePerformanceCurrentSalesData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/sale-performances/${id}/current-sales`)
      )

      return deserialize(data, SalePerformanceCurrentSalesData)
    },
    async getSalePerformanceCommissionSummary(
      opt: Pick<PaginationOpt, 'startDate' | 'endDate'>
    ): Promise<SaleCommissionSummaryData> {
      const startDate = opt.startDate ?? ''
      const endDate = opt.endDate ?? ''
      const todayTime = toDayTime(new Date().toISOString()) ?? Date.now()
      const filterStartTime = toDayTime(startDate)
      const filterEndTime = toDayTime(endDate)

      const [summaryRows, targetRows] = await Promise.all([
        fetchPostgrestRows<Record<string, unknown>>({
          resource: 'sale_commission_summary_daily_mv',
          select:
            'sale_id,staff_code,staff_name,commission_source,revenue_satang,commission_satang',
          order: 'staff_code.asc,sale_id.asc,commission_source.asc',
          filters: [`receipt_date=gte.${startDate}`, `receipt_date=lte.${endDate}`],
        }),
        fetchPostgrestRows<Record<string, unknown>>({
          resource: 'sale_commission_summary_staff_target_view',
          select:
            'sale_id,staff_code,staff_name,target_start_date,target_end_date,target_days,daily_target',
          order: 'staff_code.asc,sale_id.asc',
          filters: [`target_start_date=lte.${endDate}`, `target_end_date=gte.${startDate}`],
        }),
      ])

      const staffRows = new Map<number, SaleCommissionSummaryStaffRow>()
      const expectedTargetBySaleId = new Map<number, number>()
      const sourceTotals = new Map<
        SaleCommissionSummarySourceKey,
        { revenue: number; commission: number }
      >(COMMISSION_SUMMARY_SOURCES.map((item) => [item.source, { revenue: 0, commission: 0 }]))

      targetRows.forEach((row) => {
        const saleId = toNumber(row.sale_id)
        const targetStartTime = toDayTime(row.target_start_date)
        const targetEndTime = toDayTime(row.target_end_date)
        const targetDays = toNumber(row.target_days)
        const dailyTarget = toNumber(row.daily_target)

        if (
          saleId <= 0 ||
          !filterStartTime ||
          !filterEndTime ||
          !targetStartTime ||
          !targetEndTime ||
          targetDays <= 0 ||
          dailyTarget <= 0
        ) {
          return
        }

        const targetOverlapDays = inclusiveDays(
          maxDay(filterStartTime, targetStartTime),
          minDay(filterEndTime, targetEndTime)
        )
        const expectedOverlapDays = inclusiveDays(
          maxDay(filterStartTime, targetStartTime),
          minDay(filterEndTime, targetEndTime, todayTime)
        )

        if (targetOverlapDays <= 0) return

        const targetAmount = Number((dailyTarget * targetOverlapDays).toFixed(2))
        const expectedTarget = Number((dailyTarget * expectedOverlapDays).toFixed(2))

        staffRows.set(saleId, {
          saleId,
          staffCode: toText(row.staff_code),
          staffName: toText(row.staff_name),
          targetAmount,
          revenue: 0,
          commission: 0,
          achievementRate: 0,
          revenueBySource: {},
          commissionBySource: {},
        })
        expectedTargetBySaleId.set(saleId, expectedTarget)
      })

      summaryRows.forEach((row) => {
        const saleId = toNumber(row.sale_id)
        if (saleId <= 0) return

        const existing = staffRows.get(saleId)
        const staffRow =
          existing ??
          ({
            saleId,
            staffCode: toText(row.staff_code),
            staffName: toText(row.staff_name),
            targetAmount: 0,
            revenue: 0,
            commission: 0,
            achievementRate: 0,
            revenueBySource: {},
            commissionBySource: {},
          } satisfies SaleCommissionSummaryStaffRow)

        const revenue = satangToBahtValue(row.revenue_satang)
        const commission = satangToBahtValue(row.commission_satang)
        const source = toCommissionSummarySource(row.commission_source)
        const sourceTotal = sourceTotals.get(source)

        staffRow.revenue += revenue
        staffRow.commission += commission
        const revenueBySource = (staffRow.revenueBySource ??= {})
        const commissionBySource = (staffRow.commissionBySource ??= {})
        revenueBySource[source] = (revenueBySource[source] ?? 0) + revenue
        commissionBySource[source] = (commissionBySource[source] ?? 0) + commission
        staffRows.set(saleId, staffRow)

        if (sourceTotal) {
          sourceTotal.revenue += revenue
          sourceTotal.commission += commission
        }
      })

      const rows = Array.from(staffRows.values())
        .map((row) => ({
          ...row,
          achievementRate:
            row.targetAmount > 0 ? Number(((row.revenue / row.targetAmount) * 100).toFixed(2)) : 0,
        }))
        .filter((row) => row.targetAmount > 0 || row.revenue > 0 || row.commission > 0)
        .sort((a, b) => b.achievementRate - a.achievementRate)

      return {
        totalCommission: rows.reduce((sum, row) => sum + row.commission, 0),
        totalRevenue: rows.reduce((sum, row) => sum + row.revenue, 0),
        totalTarget: rows.reduce((sum, row) => sum + row.targetAmount, 0),
        totalExpectedTarget: rows.reduce(
          (sum, row) => sum + (expectedTargetBySaleId.get(row.saleId) ?? 0),
          0
        ),
        sourceBreakdown: COMMISSION_SUMMARY_SOURCES.map((item) => ({
          source: item.source,
          label: item.label,
          revenue: sourceTotals.get(item.source)?.revenue ?? 0,
          commission: sourceTotals.get(item.source)?.commission ?? 0,
        })),
        staffRows: rows,
      }
    },
    async getSalePerformanceSalesOverviewValue(
      id: string | number,
      opt: Pick<PaginationOpt, 'startDate' | 'endDate'> & { type?: 'TOTAL' | 'PRODUCT' | 'PACKAGE' }
    ): Promise<SalePerformanceValueData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/sale-performances/${id}/sales-overview-values`, {
          params: opt,
        })
      )

      return deserialize(data, SalePerformanceValueData)
    },
    async getSalePerformanceCommissionOverviewValues(
      id: string | number,
      opt: Pick<PaginationOpt, 'startDate' | 'endDate'>
    ): Promise<SalePerformanceCommissionOverviewData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/sale-performances/${id}/commission-overview-values`, {
          params: opt,
        })
      )

      return deserialize(data, SalePerformanceCommissionOverviewData)
    },
    async getSalePerformancePackageTypeSales(
      id: string | number,
      opt: Pick<PaginationOpt, 'startDate' | 'endDate'>
    ): Promise<SalePerformancePackageTypeSalesData[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/sale-performances/${id}/package-type-sales`, {
          params: opt,
        })
      )

      return data?.map((item) => deserialize(item, SalePerformancePackageTypeSalesData)) ?? []
    },
    async getSalePerformancePackageTypeCommissions(
      id: string | number,
      opt: Pick<PaginationOpt, 'startDate' | 'endDate'>
    ): Promise<SalePerformancePackageTypeCommissionsData[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/sale-performances/${id}/package-type-commissions`, {
          params: opt,
        })
      )

      return data?.map((item) => deserialize(item, SalePerformancePackageTypeCommissionsData)) ?? []
    },
    async getSalePerformanceReceiptCommissions(
      id: string | number,
      opt: Pick<PaginationOpt, 'page' | 'pageSize' | 'query' | 'startDate' | 'endDate'> & {
        type: 'PACKAGE' | 'PRODUCT'
      }
    ): Promise<PaginationResponse<SalePerformanceReceiptCommissionData>> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination: Record<string, unknown>
          data?: Record<string, unknown>[]
        }>(`/sale-performances/${id}/receipt-commissions`, { params: opt })
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data:
          data?.data?.map((item) => deserialize(item, SalePerformanceReceiptCommissionData)) ?? [],
      }
    },
    async updateSalePerformanceTarget(
      id: string | number,
      body: UpdateSalePerformanceTargetPayload
    ): Promise<SalePerformanceData> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/sale-performances/${id}/target`, body)
      )

      return deserialize(data, SalePerformanceData)
    },
    async getSaleOverview(
      id: string | number,
      opt: PaginationOpt
    ): Promise<PaginationResponseWithTotals<SaleOverviewData>> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination: Record<string, unknown>
          data?: Record<string, unknown>[]
          totalMembershipSales?: number
          totalPersonalTrainerSales?: number
          totalSales?: number
          totalSwimmingSales?: number
        }>(`/sale/${id}/overview`, { params: opt })
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item) => deserialize(item, SaleOverviewData)) ?? [],
        totalMembershipSales: data?.totalMembershipSales ?? 0,
        totalPersonalTrainerSales: data?.totalPersonalTrainerSales ?? 0,
        totalSales: data?.totalSales ?? 0,
        totalSwimmingSales: data?.totalSwimmingSales ?? 0,
      }
    },
    async getSaleMemberList(
      id: string | number,
      opt: PaginationOpt & { memberStatus?: string }
    ): Promise<
      PaginationResponse<SaleMemberData> & {
        counts: { all: number; active: number; expired: number; banned: number; freeze: number }
      }
    > {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination: Record<string, unknown>
          data?: Record<string, unknown>[]
          counts?: Record<string, unknown>
        }>(`/sale/${id}/members`, { params: { ...opt } })
      )
      const counts = data?.counts ?? {}
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item) => deserialize(item, SaleMemberData)) ?? [],
        counts: {
          all: Number(counts.all) || 0,
          active: Number(counts.active) || 0,
          expired: Number(counts.expired) || 0,
          banned: Number(counts.banned) || 0,
          freeze: Number(counts.freeze) || 0,
        },
      }
    },
    async assignMembersToSale(
      id: string | number,
      leadIds: number[]
    ): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/sale/${id}/assign-lead`, { leadIds })
      )
      return { success: !!data?.success }
    },
    async reassignLeadsToSale(
      targetSaleId: string | number,
      leadIds: number[]
    ): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/sale/${targetSaleId}/reassign-lead`, { leadIds })
      )
      return { success: !!data?.success }
    },
    async reassignUpsellsToSale(
      targetSaleId: string | number,
      upsellIds: string[]
    ): Promise<{ success: boolean }> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(`/sale/${targetSaleId}/reassign-upsell`, { upsellIds })
      )
      return { success: !!data?.success }
    },
    async getSaleTodoList(opt: PaginationOpt): Promise<PaginationResponse<SaleTodoData>> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination: Record<string, unknown>
          data?: Record<string, unknown>[]
        }>('/sale/todo-lists', { params: opt })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item) => deserialize(item, SaleTodoData)) ?? [],
      }
    },
    // ─── Sales Portfolio Management ──────────────────────────────────────
    async getSalePortfolio(opt?: { branchId?: number }): Promise<SalePortfolioStaffData[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ data?: Record<string, unknown>[] }>('/sale/portfolio', { params: opt })
      )
      return data?.data?.map((item) => deserialize(item, SalePortfolioStaffData)) ?? []
    },
    async getSalePortfolioLeads(
      saleId: number | string,
      opt: Pick<PaginationOpt, 'page' | 'pageSize' | 'query'>
    ): Promise<{
      pagination: PageInfo
      data: SalePortfolioLeadRowData[]
      summary: SalePortfolioListSummary
    }> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination: Record<string, unknown>
          data?: Record<string, unknown>[]
          summary: Record<string, unknown>
        }>(`/sale/${saleId}/portfolio/leads`, { params: opt })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item) => deserialize(item, SalePortfolioLeadRowData)) ?? [],
        summary: deserialize(data?.summary, SalePortfolioListSummary),
      }
    },
    async getSalePortfolioMembers(
      saleId: number | string,
      opt: Pick<PaginationOpt, 'page' | 'pageSize' | 'query'>
    ): Promise<{
      pagination: PageInfo
      data: SalePortfolioMemberRowData[]
      summary: SalePortfolioListSummary
    }> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination: Record<string, unknown>
          data?: Record<string, unknown>[]
          summary: Record<string, unknown>
        }>(`/sale/${saleId}/portfolio/members`, { params: opt })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item) => deserialize(item, SalePortfolioMemberRowData)) ?? [],
        summary: deserialize(data?.summary, SalePortfolioListSummary),
      }
    },
    async reassignSalePortfolio(
      body: SalePortfolioReassignPayload
    ): Promise<SalePortfolioReassignResponse> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/sale/portfolio/reassign', body)
      )
      return deserialize(data, SalePortfolioReassignResponse)
    },
    /**
     * The Account & Finance commission report as one Excel workbook — the
     * per-salesperson summary and the receipt-item detail behind it, cut on the
     * same staff / search filters the report page carries.
     */
    async downloadSaleCommissionReportWorkbook(params: {
      startDate: string
      endDate: string
      staffIds?: number[]
      query?: string
      lang: ReportLang
    }): Promise<Blob> {
      const { staffIds, query, ...rest } = params
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/sale-performances/commission-report/workbook', {
          params: {
            ...rest,
            ...(staffIds?.length ? { staffIds: staffIds.join(',') } : {}),
            ...(query ? { query } : {}),
          },
          responseType: 'blob',
          headers: { Accept: XLSX_MIME },
        })
      )
      return data
    },
    async downloadSalePerformanceReceiptCommissionsExcel(
      id: string | number,
      opt: Pick<PaginationOpt, 'query' | 'startDate' | 'endDate'> & {
        type: 'PACKAGE' | 'PRODUCT'
      }
    ): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>(`/sale-performances/${id}/receipt-commissions/report/excel`, {
          params: opt,
          responseType: 'blob',
          headers: { Accept: XLSX_MIME },
        })
      )

      return data
    },
    async downloadSaleMemberListExcel(id: string | number): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>(`/sale/${id}/members/report/excel`, {
          responseType: 'blob',
          headers: { Accept: XLSX_MIME },
        })
      )
      return data
    },
    async downloadSaleLeadListExcel(id: string | number): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>(`/sale/${id}/leads/report/excel`, {
          responseType: 'blob',
          headers: { Accept: XLSX_MIME },
        })
      )
      return data
    },
    async downloadSaleUpSellListExcel(id: string | number): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>(`/sale/${id}/up-sells/report/excel`, {
          responseType: 'blob',
          headers: { Accept: XLSX_MIME },
        })
      )
      return data
    },
  }
}
